/**
 * Historical Weather Data Service
 * 
 * Provides average historical weather data for destinations
 * Used for packing recommendations when trip is far in the future
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/functions';

export type HistoricalWeatherData = {
  month: number; // 1-12
  avgTemp: number;
  minTemp: number;
  maxTemp: number;
  avgPrecipitation: number; // mm
  rainyDays: number; // Average number of rainy days per month
  source?: 'openweather' | 'calculated'; // Source of the data
};

export type HistoricalWeatherResponse = {
  location: {
    city: string;
    country: string;
    latitude: number;
    longitude: number;
  };
  historical: HistoricalWeatherData[];
};

type GetHistoricalWeatherRequest = {
  latitude: number;
  longitude: number;
  city?: string;
  countryCode?: string;
  month: number; // 1-12
};

/**
 * Get historical weather data for a destination
 * Uses OpenWeatherMap historical data API or static averages
 */
export async function getHistoricalWeather(
  latitude: number,
  longitude: number,
  city?: string,
  countryCode?: string,
  month?: number
): Promise<HistoricalWeatherData | null> {
  try {
    // If month not provided, use current month or trip month
    const targetMonth = month || new Date().getMonth() + 1;

    // Prefer local city/country normals when we recognize the location.
    // This avoids Cloud Function "calculated" heuristics for ambiguous geocodes (e.g. small towns / wrong matches).
    const local = getStaticHistoricalWeather(latitude, targetMonth, city, countryCode);
    const normalized = (value?: string) =>
      (value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z\s]/g, '')
        .trim();
    const cityKey = normalized(city);
    const countryKey = normalized(countryCode);
    const usesCityOrCountryNormals =
      !!cityKey &&
        (cityKey.includes('istanbul') ||
          cityKey.includes('cappadocia') ||
          cityKey.includes('nevsehir') ||
          cityKey.includes('vancouver') ||
          cityKey.includes('toronto') ||
          cityKey.includes('montreal') ||
          cityKey.includes('calgary')) ||
      countryKey === 'tr' ||
      countryKey === 'turkiye' ||
      countryKey === 'turkey' ||
      countryKey === 'ca' ||
      countryKey === 'canada';
    if (local && usesCityOrCountryNormals) {
      return local;
    }

    // Try to get from Cloud Function (which can use OpenWeatherMap historical API)
    try {
      const getHistoricalWeatherCallable = httpsCallable<
        GetHistoricalWeatherRequest,
        HistoricalWeatherData
      >(functions, 'getHistoricalWeather');

      const result = await getHistoricalWeatherCallable({
        latitude,
        longitude,
        city,
        countryCode,
        month: targetMonth,
      });

      if (result.data) {
        return result.data;
      }
    } catch (error) {
      console.warn('[HistoricalWeather] Cloud Function call failed, using static data:', error);
    }

    // Fallback to static historical averages based on latitude/climate zone
    return getStaticHistoricalWeather(latitude, targetMonth, city, countryCode);
  } catch (error) {
    console.error('[HistoricalWeather] Error getting historical weather:', error);
    return null;
  }
}

/**
 * Get static historical weather data based on latitude and month
 * This is a simplified fallback when API is unavailable
 */
function getStaticHistoricalWeather(
  latitude: number,
  month: number,
  city?: string,
  countryCode?: string
): HistoricalWeatherData | null {
  const normalized = (value?: string) =>
    (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z\s]/g, '')
      .trim();

  const cityKey = normalized(city);
  const countryKey = normalized(countryCode);
  const tokens = cityKey.split(/\s+/).filter(Boolean);

  // City-specific climate normals (helps avoid overly-generic latitude heuristics).
  // Sources: rough climatology averages (intentionally conservative; better than crude latitude-only model).
  const CITY_MONTH_NORMALS: Record<
    string,
    Array<{ avgTemp: number; minTemp: number; maxTemp: number; avgPrecipitation: number; rainyDays: number }>
  > = {
    chicago: [
      { avgTemp: -3, minTemp: -8, maxTemp: 2, avgPrecipitation: 55, rainyDays: 10 }, // Jan
      { avgTemp: -1, minTemp: -6, maxTemp: 3, avgPrecipitation: 45, rainyDays: 9 }, // Feb
      { avgTemp: 5, minTemp: 0, maxTemp: 10, avgPrecipitation: 60, rainyDays: 10 }, // Mar
      { avgTemp: 11, minTemp: 5, maxTemp: 17, avgPrecipitation: 80, rainyDays: 11 }, // Apr
      { avgTemp: 17, minTemp: 11, maxTemp: 23, avgPrecipitation: 90, rainyDays: 11 }, // May
      { avgTemp: 23, minTemp: 17, maxTemp: 28, avgPrecipitation: 95, rainyDays: 10 }, // Jun
      { avgTemp: 26, minTemp: 20, maxTemp: 31, avgPrecipitation: 85, rainyDays: 9 }, // Jul
      { avgTemp: 25, minTemp: 19, maxTemp: 30, avgPrecipitation: 90, rainyDays: 9 }, // Aug
      { avgTemp: 21, minTemp: 14, maxTemp: 26, avgPrecipitation: 75, rainyDays: 8 }, // Sep
      { avgTemp: 14, minTemp: 8, maxTemp: 19, avgPrecipitation: 70, rainyDays: 9 }, // Oct
      { avgTemp: 6, minTemp: 1, maxTemp: 11, avgPrecipitation: 65, rainyDays: 10 }, // Nov
      { avgTemp: -1, minTemp: -6, maxTemp: 3, avgPrecipitation: 60, rainyDays: 11 }, // Dec
    ],
    vancouver: [
      { avgTemp: 6, minTemp: 2, maxTemp: 8, avgPrecipitation: 170, rainyDays: 17 }, // Jan
      { avgTemp: 7, minTemp: 2, maxTemp: 9, avgPrecipitation: 120, rainyDays: 14 }, // Feb
      { avgTemp: 9, minTemp: 4, maxTemp: 11, avgPrecipitation: 105, rainyDays: 15 }, // Mar
      { avgTemp: 11, minTemp: 6, maxTemp: 14, avgPrecipitation: 75, rainyDays: 13 }, // Apr
      { avgTemp: 14, minTemp: 9, maxTemp: 18, avgPrecipitation: 60, rainyDays: 11 }, // May
      { avgTemp: 17, minTemp: 12, maxTemp: 21, avgPrecipitation: 50, rainyDays: 9 }, // Jun
      { avgTemp: 19, minTemp: 14, maxTemp: 23, avgPrecipitation: 35, rainyDays: 6 }, // Jul
      { avgTemp: 19, minTemp: 14, maxTemp: 23, avgPrecipitation: 40, rainyDays: 6 }, // Aug
      { avgTemp: 16, minTemp: 11, maxTemp: 20, avgPrecipitation: 55, rainyDays: 8 }, // Sep
      { avgTemp: 11, minTemp: 7, maxTemp: 14, avgPrecipitation: 115, rainyDays: 14 }, // Oct
      { avgTemp: 7, minTemp: 3, maxTemp: 9, avgPrecipitation: 175, rainyDays: 17 }, // Nov
      { avgTemp: 5, minTemp: 1, maxTemp: 7, avgPrecipitation: 170, rainyDays: 18 }, // Dec
    ],
    canada: [
      { avgTemp: 6, minTemp: 2, maxTemp: 8, avgPrecipitation: 170, rainyDays: 17 },
      { avgTemp: 7, minTemp: 2, maxTemp: 9, avgPrecipitation: 120, rainyDays: 14 },
      { avgTemp: 9, minTemp: 4, maxTemp: 11, avgPrecipitation: 105, rainyDays: 15 },
      { avgTemp: 11, minTemp: 6, maxTemp: 14, avgPrecipitation: 75, rainyDays: 13 },
      { avgTemp: 14, minTemp: 9, maxTemp: 18, avgPrecipitation: 60, rainyDays: 11 },
      { avgTemp: 17, minTemp: 12, maxTemp: 21, avgPrecipitation: 50, rainyDays: 9 },
      { avgTemp: 19, minTemp: 14, maxTemp: 23, avgPrecipitation: 35, rainyDays: 6 },
      { avgTemp: 19, minTemp: 14, maxTemp: 23, avgPrecipitation: 40, rainyDays: 6 },
      { avgTemp: 16, minTemp: 11, maxTemp: 20, avgPrecipitation: 55, rainyDays: 8 },
      { avgTemp: 11, minTemp: 7, maxTemp: 14, avgPrecipitation: 115, rainyDays: 14 },
      { avgTemp: 7, minTemp: 3, maxTemp: 9, avgPrecipitation: 175, rainyDays: 17 },
      { avgTemp: 5, minTemp: 1, maxTemp: 7, avgPrecipitation: 170, rainyDays: 18 },
    ],
    istanbul: [
      { avgTemp: 8, minTemp: 4, maxTemp: 10, avgPrecipitation: 100, rainyDays: 12 }, // Jan
      { avgTemp: 9, minTemp: 4, maxTemp: 11, avgPrecipitation: 80, rainyDays: 10 }, // Feb
      { avgTemp: 11, minTemp: 6, maxTemp: 14, avgPrecipitation: 70, rainyDays: 9 }, // Mar
      { avgTemp: 15, minTemp: 9, maxTemp: 19, avgPrecipitation: 50, rainyDays: 7 }, // Apr
      { avgTemp: 20, minTemp: 14, maxTemp: 24, avgPrecipitation: 35, rainyDays: 5 }, // May
      { avgTemp: 25, minTemp: 19, maxTemp: 29, avgPrecipitation: 35, rainyDays: 4 }, // Jun
      { avgTemp: 28, minTemp: 22, maxTemp: 32, avgPrecipitation: 30, rainyDays: 3 }, // Jul
      { avgTemp: 28, minTemp: 22, maxTemp: 32, avgPrecipitation: 35, rainyDays: 3 }, // Aug
      { avgTemp: 24, minTemp: 19, maxTemp: 28, avgPrecipitation: 45, rainyDays: 4 }, // Sep
      { avgTemp: 19, minTemp: 14, maxTemp: 23, avgPrecipitation: 70, rainyDays: 7 }, // Oct
      { avgTemp: 14, minTemp: 10, maxTemp: 17, avgPrecipitation: 80, rainyDays: 9 }, // Nov
      { avgTemp: 10, minTemp: 6, maxTemp: 13, avgPrecipitation: 105, rainyDays: 12 }, // Dec
    ],
    nevsehir: [
      { avgTemp: -1, minTemp: -5, maxTemp: 3, avgPrecipitation: 45, rainyDays: 9 }, // Jan
      { avgTemp: 1, minTemp: -4, maxTemp: 5, avgPrecipitation: 40, rainyDays: 8 }, // Feb
      { avgTemp: 5, minTemp: 0, maxTemp: 10, avgPrecipitation: 40, rainyDays: 8 }, // Mar
      { avgTemp: 10, minTemp: 4, maxTemp: 16, avgPrecipitation: 45, rainyDays: 8 }, // Apr
      { avgTemp: 15, minTemp: 8, maxTemp: 22, avgPrecipitation: 45, rainyDays: 8 }, // May
      { avgTemp: 19, minTemp: 12, maxTemp: 26, avgPrecipitation: 30, rainyDays: 5 }, // Jun
      { avgTemp: 22, minTemp: 15, maxTemp: 30, avgPrecipitation: 10, rainyDays: 2 }, // Jul
      { avgTemp: 22, minTemp: 15, maxTemp: 30, avgPrecipitation: 10, rainyDays: 2 }, // Aug
      { avgTemp: 18, minTemp: 11, maxTemp: 25, avgPrecipitation: 20, rainyDays: 4 }, // Sep
      { avgTemp: 12, minTemp: 6, maxTemp: 18, avgPrecipitation: 30, rainyDays: 6 }, // Oct
      { avgTemp: 6, minTemp: 1, maxTemp: 11, avgPrecipitation: 35, rainyDays: 7 }, // Nov
      { avgTemp: 1, minTemp: -3, maxTemp: 5, avgPrecipitation: 45, rainyDays: 9 }, // Dec
    ],
    // Common “Cappadocia” searches often resolve to Göreme/Uçhisar/Nevşehir. Map them to Nevşehir normals.
    cappadocia: [
      { avgTemp: -1, minTemp: -5, maxTemp: 3, avgPrecipitation: 45, rainyDays: 9 },
      { avgTemp: 1, minTemp: -4, maxTemp: 5, avgPrecipitation: 40, rainyDays: 8 },
      { avgTemp: 5, minTemp: 0, maxTemp: 10, avgPrecipitation: 40, rainyDays: 8 },
      { avgTemp: 10, minTemp: 4, maxTemp: 16, avgPrecipitation: 45, rainyDays: 8 },
      { avgTemp: 15, minTemp: 8, maxTemp: 22, avgPrecipitation: 45, rainyDays: 8 },
      { avgTemp: 19, minTemp: 12, maxTemp: 26, avgPrecipitation: 30, rainyDays: 5 },
      { avgTemp: 22, minTemp: 15, maxTemp: 30, avgPrecipitation: 10, rainyDays: 2 },
      { avgTemp: 22, minTemp: 15, maxTemp: 30, avgPrecipitation: 10, rainyDays: 2 },
      { avgTemp: 18, minTemp: 11, maxTemp: 25, avgPrecipitation: 20, rainyDays: 4 },
      { avgTemp: 12, minTemp: 6, maxTemp: 18, avgPrecipitation: 30, rainyDays: 6 },
      { avgTemp: 6, minTemp: 1, maxTemp: 11, avgPrecipitation: 35, rainyDays: 7 },
      { avgTemp: 1, minTemp: -3, maxTemp: 5, avgPrecipitation: 45, rainyDays: 9 },
    ],
    // Country-level fallback for "Turkey" when city isn't known (use Istanbul-like coastal climate as safer default).
    turkey: [
      { avgTemp: 8, minTemp: 4, maxTemp: 10, avgPrecipitation: 95, rainyDays: 11 },
      { avgTemp: 9, minTemp: 4, maxTemp: 11, avgPrecipitation: 75, rainyDays: 9 },
      { avgTemp: 11, minTemp: 6, maxTemp: 14, avgPrecipitation: 65, rainyDays: 8 },
      { avgTemp: 15, minTemp: 9, maxTemp: 19, avgPrecipitation: 50, rainyDays: 7 },
      { avgTemp: 20, minTemp: 14, maxTemp: 24, avgPrecipitation: 40, rainyDays: 6 },
      { avgTemp: 25, minTemp: 19, maxTemp: 29, avgPrecipitation: 30, rainyDays: 4 },
      { avgTemp: 28, minTemp: 22, maxTemp: 32, avgPrecipitation: 20, rainyDays: 3 },
      { avgTemp: 28, minTemp: 22, maxTemp: 32, avgPrecipitation: 20, rainyDays: 3 },
      { avgTemp: 24, minTemp: 19, maxTemp: 28, avgPrecipitation: 35, rainyDays: 4 },
      { avgTemp: 19, minTemp: 14, maxTemp: 23, avgPrecipitation: 55, rainyDays: 7 },
      { avgTemp: 14, minTemp: 10, maxTemp: 17, avgPrecipitation: 70, rainyDays: 9 },
      { avgTemp: 10, minTemp: 6, maxTemp: 13, avgPrecipitation: 90, rainyDays: 11 },
    ],
  };

  const idx = Math.min(Math.max(month, 1), 12) - 1;
  const firstToken = tokens[0] || '';

  const resolveNormals = (key: string) => {
    if (!key) return undefined;
    if (CITY_MONTH_NORMALS[key]?.[idx]) return CITY_MONTH_NORMALS[key][idx];
    // Fuzzy contains match (handles "istanbul turkey", "istanbul province", "nevsehir cappadocia", etc.)
    const matchKey = Object.keys(CITY_MONTH_NORMALS).find((k) => key.includes(k));
    if (matchKey && CITY_MONTH_NORMALS[matchKey]?.[idx]) return CITY_MONTH_NORMALS[matchKey][idx];
    return undefined;
  };

  const directCity = resolveNormals(cityKey) || resolveNormals(firstToken);

  // Country normalizations (handles TR / Türkiye / Turkey / Canada)
  const normalizedCountry =
    countryKey === 'tr' || countryKey === 'turkiye' || countryKey === 'turkey'
      ? 'turkey'
      : countryKey === 'ca' || countryKey === 'canada'
        ? 'canada'
        : countryKey;
  const countryFallback = resolveNormals(normalizedCountry);

  if (directCity) return { month, ...directCity };
  if (countryFallback) return { month, ...countryFallback };

  // Simple climate zone estimation based on latitude
  const isTropical = Math.abs(latitude) < 23.5; // Between Tropic of Cancer and Capricorn
  const isTemperate = Math.abs(latitude) >= 23.5 && Math.abs(latitude) < 66.5;
  const isPolar = Math.abs(latitude) >= 66.5;
  const isNorthern = latitude > 0;

  // Calculate approximate temperature based on latitude and month
  // This is a simplified model - in production, use actual historical data
  let baseTemp = 20; // Base temperature at equator
  
  if (isTropical) {
    // Tropical: warm year-round, slight seasonal variation
    baseTemp = 25 + (isNorthern ? Math.sin((month - 3) * Math.PI / 6) * 3 : Math.sin((month - 9) * Math.PI / 6) * 3);
  } else if (isTemperate) {
    // Temperate: seasonal variation with a mild latitude adjustment (avoid freezing mid-latitude cities).
    const seasonalOffset = isNorthern
      ? Math.sin((month - 3) * Math.PI / 6) * 15 // Peak in July (month 7)
      : Math.sin((month - 9) * Math.PI / 6) * 15; // Peak in January (month 1)
    const latitudeAdjustment = Math.max(0, Math.abs(latitude) - 23.5) * 0.15;
    baseTemp = 14 + seasonalOffset - latitudeAdjustment;
  } else if (isPolar) {
    // Polar: very cold, less variation
    baseTemp = -10 - (Math.abs(latitude) - 66.5) * 0.8;
    const seasonalOffset = isNorthern
      ? Math.sin((month - 3) * Math.PI / 6) * 10
      : Math.sin((month - 9) * Math.PI / 6) * 10;
    baseTemp += seasonalOffset;
  }

  // Estimate precipitation (tropical = more rain, polar = less)
  let avgPrecipitation = 50; // mm/month average
  let rainyDays = 8; // days/month average
  
  if (isTropical) {
    // Deterministic wet/dry season approximation (avoid randomness so UI doesn't "jump").
    const seasonality = Math.abs(Math.sin((month - (isNorthern ? 7 : 1)) * Math.PI / 6)); // peak around mid-year
    avgPrecipitation = 90 + seasonality * 90;
    rainyDays = 10 + Math.round(seasonality * 10);
  } else if (isTemperate) {
    // More rain in winter months (opposite seasons for hemispheres)
    const isWinter = isNorthern 
      ? (month >= 11 || month <= 2)
      : (month >= 5 && month <= 8);
    avgPrecipitation = isWinter ? 80 : 40;
    rainyDays = isWinter ? 10 : 6;
  } else {
    avgPrecipitation = 20;
    rainyDays = 3;
  }

  return {
    month,
    avgTemp: Math.round(baseTemp),
    minTemp: Math.round(baseTemp - 5),
    maxTemp: Math.round(baseTemp + 5),
    avgPrecipitation: Math.round(avgPrecipitation),
    rainyDays: Math.round(rainyDays),
  };
}

/**
 * Get historical weather for a trip's destination and dates
 */
export async function getHistoricalWeatherForTrip(
  trip: { startDate: Date; endDate: Date; destination?: string; destinations?: Array<{ city: string; country?: string }> },
  coordinates?: { latitude: number; longitude: number; city: string; country: string }
): Promise<HistoricalWeatherData | null> {
  if (!coordinates) {
    const destination = trip.destination || trip.destinations?.[0]?.city;
    if (!destination) return null;

    const { getDestinationCoordinates } = await import('@/services/weather');
    const coords = await getDestinationCoordinates(destination);
    if (!coords) return null;
    coordinates = coords;
  }

  // Use the month of the trip start date
  const tripStart = trip.startDate instanceof Date ? trip.startDate : new Date(trip.startDate);
  const month = tripStart.getMonth() + 1; // getMonth() returns 0-11

  return getHistoricalWeather(
    coordinates.latitude,
    coordinates.longitude,
    coordinates.city,
    coordinates.country,
    month
  );
}

